'use client';

interface SpinnerProps {
  /**
   * Diameter in pixels. When omitted, sizing falls back to `className`
   * (e.g. `w-4 h-4`), defaulting to `w-4 h-4` if none is given.
   */
  size?: number;
  /** Extra classes — e.g. a `text-*` color, since the spinner inherits `currentColor`. */
  className?: string;
}

/**
 * Plain rotating spinner for inline loading states (buttons, badges,
 * "load more", lists, modal actions). Inherits color from `currentColor`, so it
 * shows up correctly on both light buttons (text-black) and dark surfaces.
 *
 * The animated Ultragoon logo (DashboardLoader/UltragoonLoader) is reserved for
 * full-screen scene transitions and should NOT be used as an inline spinner.
 */
export default function Spinner({ size, className = '' }: SpinnerProps) {
  const hasPxSize = typeof size === 'number';
  // When no explicit px size and no sizing class is supplied, fall back to w-4 h-4.
  const needsDefaultSize = !hasPxSize && !/\b(w-|h-|size-)/.test(className);

  return (
    <svg
      className={`animate-spin ${needsDefaultSize ? 'w-4 h-4 ' : ''}${className}`.trim()}
      style={hasPxSize ? { width: size, height: size } : undefined}
      fill="none"
      viewBox="0 0 24 24"
      role="status"
      aria-label="Loading"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
      <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}
